/** A section starting this close to the bottom of the window counts as not in view. */
const BOTTOM_SLACK = 160;
/** A revealed section's top may sit this far down the window (its `scroll-mt-*` plus slack). */
const SETTLED_TOP = 48;
/** How long to keep following lazily loaded content below the section. */
const FOLLOW_MS = 2500;

/** Scrolls the section to the top unless it is already there. */
function scrollToTop(el: Element): void {
  if (el.getBoundingClientRect().top > SETTLED_TOP) {
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}

/**
 * Scrolls a section to the top of the window, but only when its top is off screen (or just
 * peeking in at the bottom). Used when a click changes another section of the batch page, so the
 * reader sees what changed, while a click on something already in view never makes the page jump.
 * Give the section a `scroll-mt-*` so it doesn't land flush against the top edge.
 */
export function revealSection(el: Element | null): void {
  if (!el) return;
  const { top } = el.getBoundingClientRect();
  if (top >= 0 && top <= window.innerHeight - BOTTOM_SLACK) return;
  el.scrollIntoView({ behavior: 'smooth', block: 'start' });

  // A tab that loads lazily is still short at first, so the page can't scroll far enough yet.
  // Follow the page as it grows and finish the scroll — until the reader scrolls themselves.
  const content = el.parentElement;
  if (!content || typeof ResizeObserver === 'undefined') return;
  const observer = new ResizeObserver(() => {
    if (el.isConnected) scrollToTop(el);
  });
  const stop = () => {
    observer.disconnect();
    window.removeEventListener('wheel', stop);
    window.removeEventListener('touchmove', stop);
    window.removeEventListener('keydown', stop);
  };
  observer.observe(content);
  window.addEventListener('wheel', stop, { passive: true });
  window.addEventListener('touchmove', stop, { passive: true });
  window.addEventListener('keydown', stop);
  window.setTimeout(stop, FOLLOW_MS);
}

/** Same as `revealSection`, by DOM id. */
export function revealSectionById(id: string): void {
  revealSection(document.getElementById(id));
}
